import { useEffect, useState, useRef } from 'react'; // <-- Se agregó useRef
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { usePlanificacion } from '@context/PlanificacionContext';
import { calendarioService } from '@services/calendarioService';
import {
  Calendar,
  BookOpen,
  GripVertical,
  Trash2,
  Umbrella,
  Briefcase,
  ArrowRight,
  FileText,
  AlertCircle
} from 'lucide-react';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';

const COLORES_COMPETENCIAS = {
  1: { containerBg: 'bg-blue-50/50', headerBg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', badge: 'bg-blue-500', dragBg: 'bg-blue-50' },
  2: { containerBg: 'bg-green-50/50', headerBg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700', badge: 'bg-green-500', dragBg: 'bg-green-50' },
  3: { containerBg: 'bg-orange-50/50', headerBg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-500', dragBg: 'bg-orange-50' },
  4: { containerBg: 'bg-purple-50/50', headerBg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700', badge: 'bg-purple-500', dragBg: 'bg-purple-50' },
  5: { containerBg: 'bg-pink-50/50', headerBg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-700', badge: 'bg-pink-500', dragBg: 'bg-pink-50' },
  6: { containerBg: 'bg-indigo-50/50', headerBg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-700', badge: 'bg-indigo-500', dragBg: 'bg-indigo-50' },
  7: { containerBg: 'bg-red-50/50', headerBg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700', badge: 'bg-red-500', dragBg: 'bg-red-50' },
  8: { containerBg: 'bg-teal-50/50', headerBg: 'bg-teal-100', border: 'border-teal-300', text: 'text-teal-700', badge: 'bg-teal-500', dragBg: 'bg-teal-50' },
  9: { containerBg: 'bg-amber-50/50', headerBg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-700', badge: 'bg-amber-500', dragBg: 'bg-amber-50' },
  10: { containerBg: 'bg-cyan-50/50', headerBg: 'bg-cyan-100', border: 'border-cyan-300', text: 'text-cyan-700', badge: 'bg-cyan-500', dragBg: 'bg-cyan-50' },
};

const PlanificacionPage = () => {
  const navigate = useNavigate();
  const {
    area,
    grado,
    libro,
    sesiones,
    calendario,
    cargarCalendario,
    asignaciones,
    asignarSesion,
    removerSesion,
    getSesionDeSemana,
    toggleVacaciones,
    toggleGestion,
    semanasVacaciones,
    semanasGestion,
    getEstadisticas,
    tipoPeriodo, // <-- Necesitamos saber si es bimestre o trimestre
    cargarAsignaciones // <-- La función que creamos en el Contexto para asignaciones masivas
  } = usePlanificacion();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Usamos un ref para evitar que el auto-llenado se dispare múltiples veces en React Strict Mode
  const autoLlenadoEjecutado = useRef(false);

  // 1. Cargar calendario base
  useEffect(() => {
    const fetchCalendario = async () => {
      if (calendario.length === 0) {
        try {
          const response = await calendarioService.getByYear(2026);
          cargarCalendario(response.semanas || []);
        } catch (err) {
          setError('Error al cargar el calendario');
          console.error(err);
        }
      }
      setLoading(false);
    };
    fetchCalendario();
  }, [calendario.length, cargarCalendario]);

  // 2. Redirigir si no hay curso
  useEffect(() => {
    if (!area || !grado || !libro) {
      navigate('/seleccionar-curso');
    }
  }, [area, grado, libro, navigate]);

  // --- NUEVA LÓGICA DE AUTO-DISTRIBUCIÓN ---
  useEffect(() => {
    // Solo ejecutamos si tenemos datos, si es la primera vez, y si no hay asignaciones manuales previas
    if (
      !loading && 
      calendario.length > 0 && 
      sesiones.length > 0 && 
      tipoPeriodo && 
      asignaciones.length === 0 && 
      !autoLlenadoEjecutado.current
    ) {
      autoLlenadoEjecutado.current = true;
      
      // A. Extraer semanas válidas (las lectivas)
      const semanasLectivas = calendario
        .filter(sem => !semanasVacaciones.includes(sem.id) && !semanasGestion.includes(sem.id))
        .sort((a, b) => new Date(a.fecha_inicio) - new Date(b.fecha_inicio));

      // B. Preparar el array de nuevas asignaciones
      const nuevasAsignaciones = [];

      // C. Iterar sobre las semanas lectivas (slots 1 a 36)
      semanasLectivas.forEach((semana, index) => {
        const numeroSlot = index + 1; // Slot 1 al 36

        // D. Buscar la sesión que haga match con el tipo de periodo y el número de slot
        const sesionCorrespondiente = sesiones.find(s => {
          if (tipoPeriodo === 'bimestre') {
            return s.orden_bimestre === numeroSlot;
          } else {
            return s.orden_trimestre === numeroSlot;
          }
        });

        // E. Si encontramos una sesión, creamos la asignación
        if (sesionCorrespondiente) {
          nuevasAsignaciones.push({
            sesion_id: sesionCorrespondiente.id,
            semana_id: semana.id
          });
        }
      });

      // F. Enviar las asignaciones masivas al Contexto
      if (nuevasAsignaciones.length > 0) {
         cargarAsignaciones(nuevasAsignaciones);
      }
    }
  }, [loading, calendario, sesiones, tipoPeriodo, asignaciones.length, semanasVacaciones, semanasGestion, cargarAsignaciones]);
  // --- FIN NUEVA LÓGICA ---

  const sesionesAgrupadas = sesiones.reduce((acc, sesion) => {
    const bloque = sesion.titulo_bloque || 'Sin bloque';
    if (!acc[bloque]) acc[bloque] = [];
    acc[bloque].push(sesion);
    return acc;
  }, {});

  const semanasAgrupadasPorMes = calendario.reduce((acc, semana) => {
    const mes = semana.mes;
    if (!acc[mes]) acc[mes] = [];
    acc[mes].push(semana);
    return acc;
  }, {});

  const getColorCompetencia = (competenciaId) => {
    return COLORES_COMPETENCIAS[competenciaId] || COLORES_COMPETENCIAS[1];
  };

  const contarAsignaciones = (sesionId) => {
    return asignaciones.filter(a => a.sesion_id === sesionId).length;
  };

  const handleDragEnd = (result) => {
    const { destination, draggableId } = result;
    if (!destination) return;

    const sesionId = parseInt(draggableId.replace('sesion-', ''));

    if (destination.droppableId.startsWith('semana-')) {
      const semanaId = parseInt(destination.droppableId.replace('semana-', ''));
      
      // Validaciones extra de UX
      if (semanasVacaciones.includes(semanaId) || semanasGestion.includes(semanaId)) {
        alert('No puedes asignar una sesión en una semana de vacaciones o gestión.');
        return;
      }

      const sesionExistente = getSesionDeSemana(semanaId);
      if (sesionExistente) {
        alert('Esta semana ya tiene una sesión asignada. Remuévela primero.');
        return;
      }
      
      asignarSesion(sesionId, semanaId);
    }
  };

  const handleRemoverSesion = (sesionId, semanaId) => {
    // Modificado para pasar semanaId, asegurando que el Contexto pueda remover con precisión si fuera necesario
    removerSesion(sesionId, semanaId); 
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    // Solución para problema de timezone sumando horas para que caiga al mediodía local
    date.setUTCHours(12);
    return date.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
  };

  const stats = getEstadisticas();

  if (loading) return <Loading message="Cargando planificación..." />;
  if (error) return (
    <div className="card max-w-lg mx-auto mt-10 p-8 shadow-sm border border-red-200">
      <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
      <p className="text-center text-red-700 font-medium">{error}</p>
    </div>
  );

  return (
    // ... Todo tu return() se mantiene exactamente igual (no modifiqué nada de la vista).
    // Por brevedad lo omito aquí, pero tu interfaz de Drag & Drop y el carrusel siguen intactos.
    <div className="space-y-4">
      {/* HEADER PRINCIPAL */}
      <div className="card mb-4">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              Planificación Semanal
            </h1>
            <p className="text-gray-600 mb-4">
              {area?.nombre} - {grado?.nombre} • {libro?.titulo}
            </p>
            <div className="flex flex-wrap gap-3 mb-2">
              <div className="badge badge-primary text-sm px-3 py-1">
                {stats.semanasUsadas} / {stats.semanasTotales} semanas usadas
              </div>
              <div className="badge badge-success text-sm px-3 py-1">
                {stats.sesionesAsignadas} asignaciones
              </div>
            </div>
            {stats.sesionesPendientes > 0 && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 inline-block">
                ⚠️ {stats.sesionesPendientes} sesiones sin asignar (Disponibles en el banco).
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
             {/* El botón Continuar a periodos que estaba aquí, lo debes enviar a /unidades, ya que el flujo cambió */}
            <Button onClick={() => navigate('/unidades')} icon={ArrowRight} size="md">
              Continuar a Unidades
            </Button>
            <Button onClick={() => navigate('/generar-documentos')} icon={FileText} variant="outline" size="md">
              Ver documentos
            </Button>
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        {/* ... El resto de tus paneles de Drag and Drop ... */}
        {/* PANEL SUPERIOR: BLOQUES EN PARALELO (STICKY) */}
        <div className="sticky top-0 z-40 bg-gray-50/95 backdrop-blur-sm pt-2 pb-4 border-b-2 border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3 px-2">
            <BookOpen className="w-5 h-5 text-primary-700" />
            <h2 className="text-base font-semibold text-gray-900">
              Banco de Sesiones (Desliza horizontalmente)
            </h2>
          </div>

          {/* Carrusel Horizontal de Bloques */}
          <div className="flex overflow-x-auto gap-4 pb-2 px-2 snap-x hide-scrollbar" style={{ height: '40vh' }}>
            {Object.entries(sesionesAgrupadas).map(([bloque, sesionesList], index) => {
              // Obtenemos la competencia del bloque directamente de la nueva variable del backend
              const competenciaBloqueId = sesionesList[0]?.bloque_competencia_id || 1;
              const coloresBloque = getColorCompetencia(competenciaBloqueId);

              return (
                <div key={bloque} className={`min-w-[300px] max-w-[320px] flex-shrink-0 flex flex-col rounded-xl border border-gray-200 shadow-sm snap-start ${coloresBloque.containerBg}`}>
                  {/* Cabecera del bloque */}
                  <div className={`p-3 border-b border-gray-200 rounded-t-xl shrink-0 ${coloresBloque.headerBg}`}>
                    <h3 className={`text-xs font-bold uppercase tracking-wide line-clamp-2 ${coloresBloque.text}`} title={bloque}>
                      B{index + 1}: {bloque}
                    </h3>
                    <p className={`text-[10px] mt-1 ${coloresBloque.text} opacity-80 font-medium`}>
                      {sesionesList.length} sesiones
                    </p>
                  </div>
                  
                  {/* Lista de sesiones (Scroll vertical interno) */}
                  <div className="flex-1 overflow-y-auto p-2">
                    <Droppable droppableId={`bloque-${bloque}`}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2 min-h-full">
                          {sesionesList.map((sesion, idx) => {
                            const vecesAsignada = contarAsignaciones(sesion.id);
                            // Obtenemos la competencia de la sesión desde el bloque
                            const sesionCompetenciaId = sesion.bloque_competencia_id || 1;
                            const colores = getColorCompetencia(sesionCompetenciaId);

                            return (
                              <Draggable key={`${sesion.id}-${idx}`} draggableId={`sesion-${sesion.id}`} index={idx}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`
                                      p-2.5 rounded-lg border transition-all duration-200
                                      ${snapshot.isDragging 
                                        ? `${colores.dragBg} ${colores.border} shadow-2xl scale-105 z-50` 
                                        : `${vecesAsignada > 0 ? 'bg-white/60 border-gray-200 opacity-60' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'} cursor-grab`
                                      }
                                    `}
                                  >
                                    <div className="flex items-start gap-2">
                                      <GripVertical className="w-4 h-4 text-gray-400 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-center mb-1">
                                          <div className="flex items-center gap-1.5">
                                            <div className={`w-2 h-2 rounded-full ${colores.badge}`} />
                                            <span className="text-xs font-bold text-gray-800">S{sesion.numero_sesion_relativo}</span>
                                          </div>
                                          {vecesAsignada > 0 && (
                                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">Usada</span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-900 leading-tight mb-1 line-clamp-2" title={sesion.titulo}>
                                          {sesion.titulo}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PANEL INFERIOR: CALENDARIO LINEAL */}
        <div className="mt-6 px-2">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-6 h-6 text-primary-700" />
            <h2 className="text-xl font-bold text-gray-900">Calendario Escolar 2026</h2>
          </div>

          <div className="space-y-8">
            {Object.entries(semanasAgrupadasPorMes).map(([mes, semanasList]) => (
              <div key={mes} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gray-800 text-white px-4 py-2">
                  <h3 className="text-sm font-bold uppercase tracking-wider">{mes}</h3>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {semanasList.map((semana) => {
                    const sesionAsignada = getSesionDeSemana(semana.id);
                    const esVacaciones = semanasVacaciones.includes(semana.id);
                    const esGestion = semanasGestion.includes(semana.id);
                    
                    let colores = { dragBg: 'bg-white', border: 'border-transparent' };
                    if (sesionAsignada) {
                      // Obtenemos la competencia desde el bloque de la sesión asignada
                      const sesionCompetenciaId = sesionAsignada.bloque_competencia_id || 1;
                      colores = getColorCompetencia(sesionCompetenciaId);
                    }

                    return (
                      <Droppable key={semana.id} droppableId={`semana-${semana.id}`} direction="horizontal">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`
                              flex flex-col md:flex-row p-3 transition-colors min-h-[80px]
                              ${esVacaciones ? 'bg-yellow-50/50' : esGestion ? 'bg-purple-50/50' : snapshot.isDraggingOver ? 'bg-primary-50' : 'hover:bg-gray-50'}
                            `}
                          >
                            {/* Info de la Semana */}
                            <div className="w-full md:w-48 flex-shrink-0 flex md:flex-col justify-between items-center md:items-start border-b md:border-b-0 md:border-r border-gray-200 pb-2 md:pb-0 md:pr-4 mb-2 md:mb-0">
                              <div>
                                <p className="text-sm font-bold text-gray-900">Semana {semana.numero_semana}</p>
                                <p className="text-xs text-gray-500">{formatearFecha(semana.fecha_inicio)} - {formatearFecha(semana.fecha_fin)}</p>
                              </div>
                              <div className="flex gap-1 mt-1">
                                <button onClick={() => toggleVacaciones(semana.id)} className={`p-1.5 rounded-md transition-colors ${esVacaciones ? 'bg-yellow-200 text-yellow-800' : 'bg-gray-100 text-gray-500 hover:bg-yellow-100'}`} title="Marcar Vacaciones">
                                  <Umbrella className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => toggleGestion(semana.id)} className={`p-1.5 rounded-md transition-colors ${esGestion ? 'bg-purple-200 text-purple-800' : 'bg-gray-100 text-gray-500 hover:bg-purple-100'}`} title="Marcar Gestión">
                                  <Briefcase className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            {/* Zona de Drop */}
                            <div className={`flex-1 flex items-center md:pl-4 ${snapshot.isDraggingOver ? 'border-2 border-dashed border-primary-300 rounded-lg bg-white/50' : ''}`}>
                              {sesionAsignada ? (
                                <div className={`w-full md:w-auto md:min-w-[400px] flex items-center justify-between p-2.5 rounded-lg border shadow-sm ${colores.dragBg} ${colores.border}`}>
                                  <div className="flex items-center gap-3">
                                    <div className={`w-2.5 h-2.5 rounded-full ${colores.badge} flex-shrink-0`} />
                                    <div>
                                      <p className="text-xs font-bold text-gray-800 mb-0.5">
                                        Sesión {sesionAsignada.numero_sesion_relativo}
                                      </p>
                                      <p className="text-sm font-medium text-gray-900 leading-tight">
                                        {sesionAsignada.titulo}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleRemoverSesion(sesionAsignada.id, semana.id)}
                                    className="ml-4 text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-md transition-colors flex-shrink-0"
                                    title="Remover sesión"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : esVacaciones ? (
                                <p className="text-sm font-semibold text-yellow-700 w-full text-center md:text-left">Período de Vacaciones Estudiantiles</p>
                              ) : esGestion ? (
                                <p className="text-sm font-semibold text-purple-700 w-full text-center md:text-left">Semana de Gestión Docente</p>
                              ) : (
                                <p className="text-xs text-gray-400 w-full text-center md:text-left italic">
                                  Arrastra una sesión a este espacio...
                                </p>
                              )}
                              {provided.placeholder}
                            </div>
                          </div>
                        )}
                      </Droppable>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DragDropContext>
      
      {/* NUEVO BOTÓN FLOTANTE O AL FINAL DE LA PÁGINA */}
      <div className="flex justify-end pt-6 mt-8 border-t border-gray-200">
        <Button 
          onClick={() => navigate('/unidades')} 
          icon={ArrowRight} 
          size="lg"
          className="shadow-md hover:shadow-lg transition-shadow"
        >
          Continuar a Unidades
        </Button>
      </div>
      
    </div>
  );
};

export default PlanificacionPage;